import React, { useState, useRef, useEffect } from 'react';
import {
  MessageCircle, Settings, Zap, Phone, Key, Bot,
  CheckCircle, XCircle, Save, Copy, Check, Beaker,
  Send, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConfig, saveConfig, getWebhookUrl, testMessage,
  type WhatsappConfig,
} from '../api/whatsapp.client';
import { useAuthToken } from '../../../lib/useAuthToken';

const defaults: WhatsappConfig = {
  name: 'Principal', phone_number_id: '', waba_id: '',
  access_token: '', verify_token: 'dufiwaiter_secret',
  openai_api_key: '', openai_model: 'gpt-4o-mini',
  system_prompt: '', active: false, ai_enabled: false,
  welcome_message: '', business_name: 'DufiWaiter',
};

const WhatsappConfigPage: React.FC = () => {
  const t = useAuthToken();
  const qc = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['whatsapp', 'config'],
    queryFn: getConfig,
    enabled: !!t,
  });

  const { data: webhookData } = useQuery({
    queryKey: ['whatsapp', 'webhookUrl'],
    queryFn: getWebhookUrl,
    enabled: !!t,
  });

  const saveMut = useMutation({
    mutationFn: saveConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whatsapp', 'config'] });
      toast.success('Configuración guardada ✅');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const [form, setForm] = useState<WhatsappConfig>(defaults);
  const [copied, setCopied] = useState(false);
  const [testPhone, setTestPhone] = useState('573001234567');
  const [testMsg, setTestMsg] = useState('hola');
  const [testReply, setTestReply] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (config) setForm({ ...defaults, ...config });
  }, [config]);

  const set = (k: keyof WhatsappConfig, v: unknown) =>
    setForm(f => ({ ...f, [k]: v }));

  const handleCopy = () => {
    navigator.clipboard.writeText(webhookData?.webhook_url ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestReply('');
    try {
      const res = await testMessage(testPhone, testMsg);
      setTestReply(res.reply ?? '(sin respuesta)');
    } catch {
      toast.error('Error en el test');
    } finally {
      setTesting(false);
    }
  };

  if (isLoading) return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Módulo</p>
            <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-2">
              <MessageCircle size={28} className="text-green-500" /> WhatsApp Bot
            </h1>
            <p className="mt-1 text-sm text-muted">Configura el agente conversacional para tu negocio</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-full border border-divider px-4 py-2">
              <span className="text-sm font-semibold text-[color:var(--color-text)]">Bot activo</span>
              <div
                onClick={() => set('active', !form.active)}
                className={`relative h-6 w-11 rounded-full transition-colors ${form.active ? 'bg-green-500' : 'bg-surface-muted border border-divider'}`}
              >
                <div className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
              </div>
            </label>
          </div>
        </div>

        {/* Webhook URL */}
        <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
          <p className="mb-2 text-xs font-bold uppercase text-green-800">📌 URL del Webhook (pégala en Meta for Developers)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-xl bg-white border border-green-200 px-3 py-2 text-xs font-mono text-green-900 truncate">
              {webhookData?.webhook_url ?? 'Cargando…'}
            </code>
            <button onClick={handleCopy} className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Meta API config */}
          <div className="rounded-2xl border border-divider bg-surface p-6 space-y-4 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-bold text-[color:var(--color-text)]">
              <Phone size={16} className="text-blue-500" /> Configuración Meta API
            </h2>
            {[
              { label: 'Nombre del negocio', key: 'business_name', placeholder: 'DufiWaiter' },
              { label: 'Phone Number ID', key: 'phone_number_id', placeholder: '123456789…' },
              { label: 'WABA ID', key: 'waba_id', placeholder: 'WhatsApp Business Account ID' },
              { label: 'Verify Token', key: 'verify_token', placeholder: 'token_secreto' },
            ].map(f => (
              <div key={f.key}>
                <label className="mb-1 block text-xs font-semibold text-muted">{f.label}</label>
                <input
                  type="text"
                  value={(form as any)[f.key]}
                  onChange={e => set(f.key as keyof WhatsappConfig, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            ))}
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">Access Token (permanente)</label>
              <textarea
                rows={3}
                value={form.access_token}
                onChange={e => set('access_token', e.target.value)}
                placeholder="EAABs..."
                className="w-full resize-none rounded-xl border border-divider bg-surface-muted px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* OpenAI config */}
          <div className="rounded-2xl border border-divider bg-surface p-6 space-y-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold text-[color:var(--color-text)]">
                <Bot size={16} className="text-violet-500" /> Inteligencia Artificial (OpenAI)
              </h2>
              <label className="flex cursor-pointer items-center gap-2">
                <span className="text-xs font-semibold text-muted">Activar IA</span>
                <div
                  onClick={() => set('ai_enabled', !form.ai_enabled)}
                  className={`relative h-5 w-9 rounded-full transition-colors ${form.ai_enabled ? 'bg-violet-500' : 'bg-surface-muted border border-divider'}`}
                >
                  <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.ai_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
              </label>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">API Key OpenAI</label>
              <input
                type="password"
                value={form.openai_api_key}
                onChange={e => set('openai_api_key', e.target.value)}
                placeholder="sk-..."
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">Modelo GPT</label>
              <select
                value={form.openai_model}
                onChange={e => set('openai_model', e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="gpt-4o-mini">gpt-4o-mini (recomendado, más económico)</option>
                <option value="gpt-4o">gpt-4o (más inteligente)</option>
                <option value="gpt-3.5-turbo">gpt-3.5-turbo (legacy)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">
                Prompt del sistema
                <span className="ml-2 font-normal text-muted opacity-60">(instrucciones para la IA)</span>
              </label>
              <textarea
                rows={5}
                value={form.system_prompt}
                onChange={e => set('system_prompt', e.target.value)}
                placeholder="Eres el asistente de DufiWaiter. Atiende clientes con amabilidad, muestra el menú y toma pedidos. El catálogo se carga automáticamente..."
                className="w-full resize-none rounded-xl border border-divider bg-surface-muted px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="mt-1 text-[10px] text-muted">💡 El catálogo de productos y sabores se inyecta automáticamente. Solo define el tono y reglas especiales.</p>
            </div>
          </div>
        </div>

        {/* Welcome message */}
        <div className="rounded-2xl border border-divider bg-surface p-6 space-y-3 shadow-sm">
          <h2 className="flex items-center gap-2 text-sm font-bold text-[color:var(--color-text)]">
            <MessageCircle size={16} className="text-green-500" /> Mensaje de bienvenida
          </h2>
          <textarea
            rows={3}
            value={form.welcome_message}
            onChange={e => set('welcome_message', e.target.value)}
            placeholder={"👋 ¡Hola! Bienvenido a DufiWaiter.\n\n¿Qué deseas hoy?\n🍦 1. Menú  2. Sabores  3. Mi carrito"}
            className="w-full resize-none rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-[10px] text-muted">Deja vacío para usar el mensaje por defecto.</p>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={() => saveMut.mutate(form)}
            disabled={saveMut.isPending}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-8 text-sm font-bold text-white shadow-lg hover:opacity-90 disabled:opacity-60 transition"
          >
            <Save size={16} /> {saveMut.isPending ? 'Guardando…' : 'Guardar configuración'}
          </button>
        </div>

        {/* Test simulator */}
        <div className="rounded-2xl border border-divider bg-surface p-6 space-y-4 shadow-sm">
          <h2 className="flex items-center gap-2 text-sm font-bold text-[color:var(--color-text)]">
            <Beaker size={16} className="text-amber-500" /> Simulador de conversación
            <span className="text-[10px] text-muted font-normal">(sin enviar a WhatsApp real)</span>
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted">Teléfono simulado</label>
              <input
                type="text"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-muted">Mensaje del cliente</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={testMsg}
                  onChange={e => setTestMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTest()}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 disabled:opacity-60 transition"
                >
                  {testing ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </div>
          </div>
          {testReply && (
            <div className="rounded-2xl bg-green-50 border border-green-200 p-4">
              <p className="mb-1 text-[10px] font-bold uppercase text-green-700">🤖 Respuesta del bot:</p>
              <pre className="whitespace-pre-wrap text-sm text-green-900 font-sans">{testReply}</pre>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default WhatsappConfigPage;
